// shared aggregation helpers used by the HTML topology mode views.
// single source of truth for "how do we compute high-risk / attacked /
// classified". the modes can disagree on layout but never on what a
// number means

import type {
  EngagementAsset,
  EngagementAttackRun,
  EngagementEvidence,
  EngagementFinding,
  EngagementPort,
  EngagementSubnet,
} from '@/lib/engagementsApi'

export type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'normal'

export interface HostAggregate {
  asset:          EngagementAsset
  subnetCidr:     string
  attackCount:    number
  highSevPorts:   number
  mediumSevPorts: number
  findings: {
    critical: number
    high:     number
    medium:   number
    low:      number
    info:     number
    total:    number
    worst:    RiskLevel
  }
  evidenceCount:  number
  topRisk:        RiskLevel
}

export interface SubnetAggregate {
  cidr:           string
  discovery_source: string | null
  hostCount:      number
  classifiedCount: number
  highRiskCount:  number
  attackedCount:  number
  findingCount:   number
  worstSeverity:  RiskLevel
  hosts:          HostAggregate[]
}

// "1.2.3.4" → "1.2.3.0/24". pure fallback when an asset row lacks the
// migration-016 subnet_cidr (legacy submissions / synthesized rows)
export function fallbackCidr(ip: string | null | undefined): string {
  if (!ip) return 'unknown'
  const parts = ip.split('.')
  if (parts.length !== 4 || parts.some(p => !/^\d+$/.test(p))) return 'unknown'
  return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`
}

const SEV_RANK: Record<string, number> = {
  CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4,
}

export function worstOf(a: RiskLevel, b: RiskLevel): RiskLevel {
  const order: RiskLevel[] = ['critical', 'high', 'medium', 'low', 'info', 'normal']
  return order.indexOf(a) <= order.indexOf(b) ? a : b
}

export function normaliseSeverity(s: string | null | undefined): RiskLevel {
  if (!s) return 'normal'
  switch (s.toUpperCase()) {
    case 'CRITICAL': return 'critical'
    case 'HIGH':     return 'high'
    case 'MEDIUM':   return 'medium'
    case 'LOW':      return 'low'
    case 'INFO':     return 'info'
    default:         return 'normal'
  }
}

// tone palette used across the HTML topology modes. centralised so a
// "high" host card and a "high" subnet card always read the same to
// the viewer
export const RISK_TONES: Record<RiskLevel, {
  border: string; bg: string; text: string; dot: string; pill: string;
}> = {
  critical: {
    border: 'border-rose-500',  bg: 'bg-rose-50',     text: 'text-rose-700',
    dot:    'bg-rose-500',      pill: 'bg-rose-100 text-rose-700',
  },
  high: {
    border: 'border-orange-500', bg: 'bg-orange-50',  text: 'text-orange-700',
    dot:    'bg-orange-500',     pill: 'bg-orange-100 text-orange-700',
  },
  medium: {
    border: 'border-amber-400',  bg: 'bg-amber-50',   text: 'text-amber-700',
    dot:    'bg-amber-400',      pill: 'bg-amber-100 text-amber-700',
  },
  low: {
    border: 'border-yellow-300', bg: 'bg-yellow-50',  text: 'text-yellow-700',
    dot:    'bg-yellow-300',     pill: 'bg-yellow-100 text-yellow-700',
  },
  info: {
    border: 'border-sky-300',    bg: 'bg-sky-50',     text: 'text-sky-700',
    dot:    'bg-sky-300',        pill: 'bg-sky-100 text-sky-700',
  },
  normal: {
    border: 'border-slate-200',  bg: 'bg-white',      text: 'text-slate-700',
    dot:    'bg-slate-300',      pill: 'bg-slate-100 text-slate-600',
  },
}

export interface AggregationResult {
  subnets:   SubnetAggregate[]
  byHostIp:  Map<string, HostAggregate>
  totals: {
    hosts:      number
    classified: number
    attacked:   number
    highRisk:   number
    findings:   { critical: number; high: number; medium: number; low: number; info: number; total: number }
  }
}

export function aggregate(
  assets:    EngagementAsset[],
  subnets:   EngagementSubnet[],
  ports:     EngagementPort[],
  attackRuns: EngagementAttackRun[],
  findings:  EngagementFinding[],
  evidence:  { asset_id: string | null; asset?: { host_ip: string | null } | null }[] = [],
): AggregationResult {
  // ── Indexes ────────────────────────────────────────────────────────
  const portsByIp = new Map<string, EngagementPort[]>()
  for (const p of ports) {
    if (!p.host_ip) continue
    const arr = portsByIp.get(p.host_ip) ?? []
    arr.push(p)
    portsByIp.set(p.host_ip, arr)
  }
  const attackTargetCounts = new Map<string, number>()
  for (const r of attackRuns) {
    if (!r.target_ip) continue
    attackTargetCounts.set(r.target_ip, (attackTargetCounts.get(r.target_ip) ?? 0) + 1)
  }
  const findingsByAssetId = new Map<string, EngagementFinding[]>()
  const findingsByIp      = new Map<string, EngagementFinding[]>()
  for (const f of findings) {
    if (f.asset_id) {
      const list = findingsByAssetId.get(f.asset_id) ?? []
      list.push(f)
      findingsByAssetId.set(f.asset_id, list)
    }
    const ip = f.asset?.host_ip
    if (ip) {
      const list = findingsByIp.get(ip) ?? []
      list.push(f)
      findingsByIp.set(ip, list)
    }
  }
  const evidenceCountByIp = new Map<string, number>()
  const evidenceCountByAssetId = new Map<string, number>()
  for (const ev of evidence) {
    if (ev.asset_id) evidenceCountByAssetId.set(ev.asset_id, (evidenceCountByAssetId.get(ev.asset_id) ?? 0) + 1)
    const ip = ev.asset?.host_ip
    if (ip) evidenceCountByIp.set(ip, (evidenceCountByIp.get(ip) ?? 0) + 1)
  }

  // ── Per-host roll-up ───────────────────────────────────────────────
  const byHostIp = new Map<string, HostAggregate>()
  for (const a of assets) {
    const myPorts = portsByIp.get(a.host_ip) ?? []
    let highSev = 0, mediumSev = 0
    for (const p of myPorts) {
      const sev = (p.severity ?? '').toUpperCase()
      if (sev === 'CRITICAL' || sev === 'HIGH') highSev++
      else if (sev === 'MEDIUM') mediumSev++
    }
    const fs = [
      ...(findingsByAssetId.get(a.id) ?? []),
      ...(findingsByIp.get(a.host_ip) ?? []),
    ]
    // dedupe, same row could match by both keys
    const seen = new Set<string>()
    const myFindings = fs.filter(f => seen.has(f.id) ? false : (seen.add(f.id), true))
    const counts = { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: myFindings.length, worst: 'normal' as RiskLevel }
    for (const f of myFindings) {
      const r = normaliseSeverity(f.severity)
      if (r in counts) (counts as any)[r]++
      counts.worst = worstOf(counts.worst, r)
    }
    const attackCount = attackTargetCounts.get(a.host_ip) ?? 0
    const evCount = (evidenceCountByAssetId.get(a.id) ?? 0) + (evidenceCountByIp.get(a.host_ip) ?? 0)
    // topRisk is "what we have evidence to call this host". findings
    // and port-severity drive it, attack runs alone DO NOT promote it.
    // an attack simulation that didn't return impact data is just
    // "we tested this host", not "this host is risky"
    let topRisk: RiskLevel = counts.worst
    if (highSev > 0) topRisk = worstOf(topRisk, 'high')
    else if (mediumSev > 0) topRisk = worstOf(topRisk, 'medium')
    byHostIp.set(a.host_ip, {
      asset: a,
      subnetCidr: a.subnet_cidr ?? fallbackCidr(a.host_ip),
      attackCount,
      highSevPorts: highSev,
      mediumSevPorts: mediumSev,
      findings: counts,
      evidenceCount: evCount,
      topRisk,
    })
  }

  // ── Subnet aggregation ─────────────────────────────────────────────
  const subnetByCidr = new Map<string, SubnetAggregate>()
  for (const s of subnets) {
    subnetByCidr.set(s.cidr, {
      cidr: s.cidr,
      discovery_source: s.discovery_source ?? null,
      hostCount: s.host_count,
      classifiedCount: s.classified_count,
      highRiskCount: s.high_risk_count,
      attackedCount: s.attacked_count,
      findingCount: 0,
      worstSeverity: 'normal',
      hosts: [],
    })
  }
  for (const h of byHostIp.values()) {
    let bucket = subnetByCidr.get(h.subnetCidr)
    if (!bucket) {
      // fallback bucket for hosts whose cidr never appeared in
      // engagement_subnets (legacy submissions / orphan /24s)
      bucket = {
        cidr: h.subnetCidr,
        discovery_source: null,
        hostCount: 0,
        classifiedCount: 0,
        highRiskCount: 0,
        attackedCount: 0,
        findingCount: 0,
        worstSeverity: 'normal',
        hosts: [],
      }
      subnetByCidr.set(h.subnetCidr, bucket)
    }
    bucket.hosts.push(h)
    bucket.findingCount += h.findings.total
    bucket.worstSeverity = worstOf(bucket.worstSeverity, h.topRisk)
  }

  // sort each subnet's hosts by risk first, then last octet for sanity
  for (const s of subnetByCidr.values()) {
    s.hosts.sort((a, b) => {
      const riskOrder: RiskLevel[] = ['critical', 'high', 'medium', 'low', 'info', 'normal']
      const da = riskOrder.indexOf(a.topRisk) - riskOrder.indexOf(b.topRisk)
      if (da !== 0) return da
      const la = lastOctet(a.asset.host_ip)
      const lb = lastOctet(b.asset.host_ip)
      return la - lb
    })
    // if counters from the subnets table are stale or zero, recompute
    // from the host roll-up so the UI is internally consistent
    if (s.hostCount === 0 && s.hosts.length > 0) s.hostCount = s.hosts.length
    if (s.classifiedCount === 0) s.classifiedCount = s.hosts.filter(h => h.asset.device_type).length
    if (s.highRiskCount === 0)   s.highRiskCount   = s.hosts.filter(h => h.highSevPorts > 0).length
    if (s.attackedCount === 0)   s.attackedCount   = s.hosts.filter(h => h.attackCount > 0).length
  }

  const subnetList = Array.from(subnetByCidr.values()).sort((a, b) => {
    // worst-severity subnets first, then by hostCount desc
    const riskOrder: RiskLevel[] = ['critical', 'high', 'medium', 'low', 'info', 'normal']
    const da = riskOrder.indexOf(a.worstSeverity) - riskOrder.indexOf(b.worstSeverity)
    if (da !== 0) return da
    return b.hostCount - a.hostCount
  })

  const totals = {
    hosts:      assets.length,
    classified: assets.filter(a => a.device_type).length,
    attacked:   Array.from(byHostIp.values()).filter(h => h.attackCount > 0).length,
    highRisk:   Array.from(byHostIp.values()).filter(h => h.topRisk === 'high' || h.topRisk === 'critical').length,
    findings: {
      critical: findings.filter(f => normaliseSeverity(f.severity) === 'critical').length,
      high:     findings.filter(f => normaliseSeverity(f.severity) === 'high').length,
      medium:   findings.filter(f => normaliseSeverity(f.severity) === 'medium').length,
      low:      findings.filter(f => normaliseSeverity(f.severity) === 'low').length,
      info:     findings.filter(f => normaliseSeverity(f.severity) === 'info').length,
      total:    findings.length,
    },
  }

  return { subnets: subnetList, byHostIp, totals }
}

function lastOctet(ip: string): number {
  const parts = ip.split('.')
  const n = parseInt(parts[parts.length - 1], 10)
  return isFinite(n) ? n : 0
}

// severity rank for sort comparators outside this module
export function severityRank(s: string | null | undefined): number {
  if (!s) return 99
  return SEV_RANK[s.toUpperCase()] ?? 99
}

// ── Defensive subnet sanitization ──────────────────────────────────────
// Dorothy's auto-route discovery emits /32 entries for the surveyor's
// own IP, broadcast addresses, and other zero-host artifacts. those
// pollute the Summary/Subnet count and confuse the user. classify each
// subnet as "display" or "ignored" so the UI can hide the noise while
// keeping the rows in DB for debugging.
//
// a subnet is ignored when ALL of the following hold:
//   1. CIDR is /32 (single host)
//   2. discovery_source is one of {auto-route, auto_route, route,
//      derived_24, derived, derived/unknown, unknown, null}
//   3. hostCount is 0 (no asset row attaches to it)
//   4. a larger same-engagement subnet covers the IP (containment by
//      /24 prefix, we don't currently submit anything wider than /24)
// also: an obvious .255 broadcast /32 is always ignored when its /24
// is present, regardless of discovery_source. catches Dorothy payloads
// where the source label is "explicit" but the row is still junk

export interface ClassifiedSubnets {
  display: SubnetAggregate[]
  ignored: SubnetAggregate[]
}

const AUTO_ROUTE_SOURCES = new Set([
  'auto-route', 'auto_route', 'auto', 'route', 'routes',
  'derived', 'derived_24', 'derived/24', 'derived/unknown',
  'unknown', '',
])

// "192.168.0.175/32" → "192.168.0", or null if not a /32.
function slash32Prefix(cidr: string): string | null {
  if (!cidr.endsWith('/32')) return null
  const ip = cidr.slice(0, -3)
  const parts = ip.split('.')
  if (parts.length !== 4 || parts.some(p => !/^\d+$/.test(p))) return null
  return `${parts[0]}.${parts[1]}.${parts[2]}`
}

// "192.168.0.0/24" → "192.168.0", or null if not a /24.
function slash24Prefix(cidr: string): string | null {
  if (!cidr.endsWith('/24')) return null
  const ip = cidr.slice(0, -3)
  const parts = ip.split('.')
  if (parts.length !== 4 || parts.some(p => !/^\d+$/.test(p))) return null
  return `${parts[0]}.${parts[1]}.${parts[2]}`
}

// last octet of a /32 cidr, returns NaN when the cidr isn't /32 IPv4
function slash32LastOctet(cidr: string): number {
  if (!cidr.endsWith('/32')) return NaN
  const ip = cidr.slice(0, -3)
  const parts = ip.split('.')
  if (parts.length !== 4) return NaN
  const n = parseInt(parts[3], 10)
  return isFinite(n) ? n : NaN
}

export function classifySubnets(all: SubnetAggregate[]): ClassifiedSubnets {
  // coverage requires only that the parent /24 EXISTS in the same
  // engagement, not that it have hosts. test4 ships a /24 with zero
  // hosts plus auto-route /32 noise. if we required the /24 to be
  // populated, the noise would survive sanitization
  const present24s = new Set<string>()
  for (const s of all) {
    const p = slash24Prefix(s.cidr)
    if (p) present24s.add(p)
  }

  const display: SubnetAggregate[] = []
  const ignored: SubnetAggregate[] = []

  for (const s of all) {
    const p32 = slash32Prefix(s.cidr)
    if (!p32) {
      // not a /32, always keep
      display.push(s)
      continue
    }
    const lastOctet = slash32LastOctet(s.cidr)
    const hasHost   = s.hosts.length > 0 || s.hostCount > 0
    // broadcast .255/32 covered by its own /24, always ignore. catches
    // the case where Dorothy mistakenly labels it "explicit"
    if (lastOctet === 255 && present24s.has(p32) && !hasHost) {
      ignored.push(s)
      continue
    }
    // general rule: zero-host /32 from a route-ish source, with a
    // same-/24 in the engagement
    const source = (s.discovery_source ?? '').toLowerCase()
    const isRouteish = AUTO_ROUTE_SOURCES.has(source)
    if (!hasHost && isRouteish && present24s.has(p32)) {
      ignored.push(s)
      continue
    }
    display.push(s)
  }

  return { display, ignored }
}

// ── Attack-edge aggregation ────────────────────────────────────────────
// group multiple attack_runs against the same target into a single
// "AttackEdgeGroup" so the Attack Path canvas can render one labelled
// edge with "Type ×N" and the drawer can show the per-run breakdown

export interface AttackEdgeGroup {
  target_ip:        string
  attack_type:      string
  runs:             EngagementAttackRun[]
  count:            number
  totalPackets:     number
  totalDuration:    number     // seconds
  firstStartTime:   string | null
  lastStopTime:     string | null
  protocols:        string[]
}

export function groupAttackRuns(runs: EngagementAttackRun[]): AttackEdgeGroup[] {
  const groups = new Map<string, AttackEdgeGroup>()
  for (const r of runs) {
    if (!r.target_ip) continue
    const type = r.attack_type ?? 'attack'
    const key  = `${r.target_ip}::${type}`
    let g = groups.get(key)
    if (!g) {
      g = {
        target_ip: r.target_ip,
        attack_type: type,
        runs: [],
        count: 0,
        totalPackets: 0,
        totalDuration: 0,
        firstStartTime: null,
        lastStopTime: null,
        protocols: [],
      }
      groups.set(key, g)
    }
    g.runs.push(r)
    g.count++
    if (r.packets_sent != null) g.totalPackets += r.packets_sent
    if (r.duration_seconds != null) g.totalDuration += r.duration_seconds
    if (r.protocol && !g.protocols.includes(r.protocol)) g.protocols.push(r.protocol)
    if (r.start_time) {
      if (!g.firstStartTime || r.start_time < g.firstStartTime) g.firstStartTime = r.start_time
    }
    if (r.stop_time) {
      if (!g.lastStopTime || r.stop_time > g.lastStopTime) g.lastStopTime = r.stop_time
    }
  }
  return Array.from(groups.values())
}

// ── Synthesized attack-target assets ──────────────────────────────────
// when Dorothy ships attack_runs but no engagement_assets row for the
// targeted IP, fabricate a stub asset so the host appears in Summary /
// Subnet View / top-risky and the rest of the aggregation pipeline can
// link evidence to it.
//
// stub assets have id `synth:asset:<ip>`, no MAC/vendor, host_name
// `"(attack target)"` so the row is visually distinct. Hosts tab stays
// on the real asset list to avoid muddying the inventory view

export function synthesizeAttackTargetAssets(
  realAssets: EngagementAsset[],
  attackRuns: EngagementAttackRun[],
): EngagementAsset[] {
  const seen = new Set<string>(realAssets.map(a => a.host_ip))
  const synth: EngagementAsset[] = []
  for (const r of attackRuns) {
    const ip = r.target_ip
    if (!ip || seen.has(ip)) continue
    seen.add(ip)
    synth.push({
      id:                      `synth:asset:${ip}`,
      host_ip:                 ip,
      host_name:               '(attack target)',
      mac_address:             null,
      vendor:                  null,
      is_online:               null,
      ping_time:               null,
      scan_time:               r.start_time ?? null,
      industrial_vendor:       null,
      industrial_category:     null,
      industrial_protocols:    null,
      hardware_id:             null,
      machine_name:            null,
      device_type:             null,
      device_type_confidence:  null,
      device_vendor:           null,
      device_model:            null,
      device_firmware_version: null,
      subnet_cidr:             null,   // aggregate() will fall back to /24
      discovery_source:        'attack_target',
      external_asset_id:       null,
      created_at:              '',
    })
  }
  return synth
}

// ── Synthesized evidence fallback ──────────────────────────────────────
// when Dorothy hasn't shipped normalized engagement_evidence rows for
// its attack_runs, fabricate display-only evidence rows from the runs
// themselves so the Evidence tab and host drawer don't look broken.
//
// synth rows have id `synth:attack:<run.id>` so they never collide with
// real engagement_evidence.id uuids. `asset` is filled when the asset
// list contains a host_ip match

export function synthesizeAttackEvidence(
  attackRuns: EngagementAttackRun[],
  existing:   EngagementEvidence[],
  assets:     EngagementAsset[],
): EngagementEvidence[] {
  // skip every run that already has a paired evidence row. match by
  // raw_json.attack_run_id when present (preferred), otherwise by the
  // attack run's id appearing anywhere in the evidence id list
  const coveredRunIds = new Set<string>()
  for (const ev of existing) {
    if (ev.evidence_type === 'attack_run') {
      const linkedId = ev.raw_json?.attack_run_id ?? ev.raw_json?.id ?? null
      if (linkedId) coveredRunIds.add(String(linkedId))
    }
  }
  const assetByIp = new Map<string, EngagementAsset>(assets.map(a => [a.host_ip, a]))
  const synth: EngagementEvidence[] = []
  for (const r of attackRuns) {
    if (coveredRunIds.has(r.id)) continue
    const a = r.target_ip ? assetByIp.get(r.target_ip) : null
    synth.push({
      id:                `synth:attack:${r.id}`,
      asset_id:          a?.id ?? null,
      external_asset_id: null,
      evidence_type:     'attack_run',
      source_tool:       'dorothy_attack_simulation',
      summary:           describeAttackRun(r),
      // no severity_hint on synthesized rows, attack simulation alone
      // is not a vulnerability and the Evidence tab doesn't need a
      // colour-coded badge for it. severity only appears if Dorothy
      // ships a real impact-bearing record
      severity_hint:     null,
      raw_json:          { ...r, _synthesized: true },
      collected_at:      r.start_time ?? null,
      asset: a ? {
        id:          a.id,
        host_ip:     a.host_ip,
        host_name:   a.host_name,
        subnet_cidr: a.subnet_cidr,
        device_type: a.device_type,
      } : null,
    })
  }
  return synth
}

// humanise Dorothy's internal attack_type strings ("TcpSynFlood",
// "UdpFlood", "IcmpFlood", "SlowLoris" …) into readable names
export function humanAttackType(raw: string | null | undefined): string {
  if (!raw) return 'Attack simulation'
  switch (raw) {
    case 'TcpSynFlood':   return 'TCP SYN Flood'
    case 'UdpFlood':      return 'UDP Flood'
    case 'IcmpFlood':     return 'ICMP Flood'
    case 'SlowLoris':     return 'Slowloris'
    case 'HttpFlood':     return 'HTTP Flood'
    case 'DnsAmplification': return 'DNS Amplification'
    default:
      // Camel/PascalCase → space-separated
      return raw.replace(/([A-Z])/g, ' $1').replace(/^\s+/, '').replace(/_/g, ' ').trim()
  }
}

// factual, report-ready phrasing. attack simulation is described as a
// test event, not an outcome, no "succeeded", no "accepted flood".
// "No observed impact recorded" appears so the reader doesn't infer
// vulnerability where none was measured
function describeAttackRun(r: EngagementAttackRun): string {
  const label = humanAttackType(r.attack_type)
  const parts: string[] = [`${label} simulation executed`]
  if (r.target_ip) {
    parts[0] += ` against ${r.target_ip}`
    if (r.target_port != null) parts[0] += `:${r.target_port}`
  }
  if (r.target_rate_mbps != null) parts.push(`at ${r.target_rate_mbps} Mbps`)
  if (r.duration_seconds != null) parts.push(`for ${r.duration_seconds} second${r.duration_seconds === 1 ? '' : 's'}`)
  let s = parts.join(' ') + '.'
  if (r.packets_sent != null) s += ` Packets sent: ${new Intl.NumberFormat().format(r.packets_sent)}.`
  // packet-rate fragment. prefer Dorothy's avg + peak when present
  const fmt = (n: number | null | undefined) =>
    n == null ? null : new Intl.NumberFormat().format(Math.round(n))
  const ppsFragments: string[] = []
  if (r.average_pps != null) ppsFragments.push(`avg ${fmt(r.average_pps)} PPS`)
  if (r.peak_pps    != null) ppsFragments.push(`peak ${fmt(r.peak_pps)} PPS`)
  if (ppsFragments.length > 0) s += ` ${ppsFragments.join(' · ')}.`
  // no vulnerability claim unless Dorothy supplies an explicit impact
  // marker. until then, surface the absence of one in factual terms
  const status = String((r as any).status ?? (r as any).result ?? '').toLowerCase()
  const note   = (r as any).observed_response ?? (r as any).manual_note ?? null
  if (note) {
    s += ` Observed: ${note}.`
  } else if (!SUCCESS_HINTS.some(k => status.includes(k))) {
    s += ' No observed impact recorded.'
  }
  return s
}

// ── Synthesized findings from attack runs (only when impact is real) ──
// conservative: spec says "only when status indicates success/impact".
// Dorothy's current payload has no success flag, so by default no
// findings are synthesized. if `raw_json` carries a status/result/
// success hint, we honour it. returns an empty array when nothing
// qualifies

const SUCCESS_HINTS = ['success', 'succeeded', 'compromised', 'impact', 'positive']
const FAILURE_HINTS = ['failed', 'no_impact', 'mitigated', 'blocked']

export function synthesizeAttackFindings(
  attackRuns: EngagementAttackRun[],
  existing:   EngagementFinding[],
  assets:     EngagementAsset[],
): EngagementFinding[] {
  const coveredRunIds = new Set<string>()
  for (const f of existing) {
    const linkedId = f.raw_json?.attack_run_id ?? f.raw_json?.id ?? null
    if (linkedId) coveredRunIds.add(String(linkedId))
  }
  const assetByIp = new Map<string, EngagementAsset>(assets.map(a => [a.host_ip, a]))
  const out: EngagementFinding[] = []
  for (const r of attackRuns) {
    if (coveredRunIds.has(r.id)) continue
    const status = String((r as any).status ?? (r as any).result ?? '').toLowerCase()
    const success = (r as any).success
    const isSuccess =
      success === true ||
      SUCCESS_HINTS.some(s => status.includes(s))
    const isFailure =
      success === false ||
      FAILURE_HINTS.some(s => status.includes(s))
    if (!isSuccess || isFailure) continue
    const a = r.target_ip ? assetByIp.get(r.target_ip) : null
    out.push({
      id:                `synth:finding:${r.id}`,
      asset_id:          a?.id ?? null,
      external_asset_id: null,
      // wording is intentionally framed as a result, not a verdict.
      // even when Dorothy's payload supplies a success/impact hint, a
      // simulation is informational until a human reviews the target
      // and assigns severity. default to INFO so the count of
      // "Critical / High findings" stays driven by real vulns (SNMP
      // sensitive community, etc.)
      title:             `Traffic simulation result: ${humanAttackType(r.attack_type)} → ${r.target_ip ?? '(target unknown)'}`,
      severity:          'INFO',
      category:          'attack-simulation',
      status:            'open',
      recommendation:    'Review target behaviour with the operator. Capture any observed impact in the Notes field before promoting this to a vulnerability finding.',
      evidence_ids:      [`synth:attack:${r.id}`],
      raw_json:          { ...r, _synthesized: true },
      created_at:        r.start_time ?? new Date().toISOString(),
      updated_at:        r.stop_time ?? r.start_time ?? new Date().toISOString(),
      asset: a ? {
        id:          a.id,
        host_ip:     a.host_ip,
        host_name:   a.host_name,
        subnet_cidr: a.subnet_cidr,
        device_type: a.device_type,
      } : null,
    })
  }
  return out
}
